import React from 'react'
import { useParams, useHistory } from 'react-router'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'

import PermissionErrorPage from '../PermissionError'
import AgentPage from '../../components/templates/AgentPage'
import LoadingSpinner from '../../components/atoms/LoadingSpinner'
import DetailsTopBar from '../../components/organisms/ContactDetails/DetailsTopBar'
import DetailsPane from '../../components/organisms/ContactDetails/DetailsPane'
import DetailsTable from '../../components/organisms/ContactDetails/DetailsTable'

import CustomerProvider from '../../providers/CustomerProvider'
import QueueStateProvider from '../../providers/QueueStateProvider'
import ChatEditStateProvider from '../../providers/ChatEditStateProvider'
import MetaProvider from '../../providers/MetaProvider'
import EditProvider from '../../providers/EditProvider'
import UserSummary from './Mobile/UserSummary'

import useAccess, { PERMS } from '../../hooks/useAccess'
import { Routes } from '../../Routes'
import { clear } from '../../slices/avatarSlice'
import { useEnvironment } from '../../providers/EnvironmentProvider'
import { useUserAPI } from '../../providers/UserAPIProvider'
import { selectRole } from '../../slices/role'
import { useBreakpoint } from '../../providers/BreakpointProvider'
import { fetchTypes, selectUserType } from '../../slices/userType'
import { AddSuccessAlert } from '../../slices/alert'

import useStyles from './styles'
import { saveContactActivity } from '@SonarCX/lib/idb'

const ContactDetails = () => {
  const { id } = useParams()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const userTypes = useAppSelector(selectUserType)
  const roleSelector = useAppSelector(selectRole)
  const {
    allUsers,
    loadUser,
    loaded: usersLoaded,
    uploadUser,
    userNotFound,
    setUserNotFound
  } = useUserAPI()
  const hasPermission = useAccess()
  const { user: currentUser, env, loaded: envLoaded } = useEnvironment()
  const { adminRole } = roleSelector
  const { down770, up770 } = useBreakpoint()

  const [user, setUser] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [img, _setImg] = React.useState(null)
  const [imageHasBeenEdited, setImageHasBeenEdited] = React.useState(false)

  const setImg = React.useCallback(
    newImg => {
      if (newImg === 'REMOVE') {
        _setImg(null)
        dispatch(clear(user?.avatar_id))
        setUser(vals => ({ ...vals, avatar_id: null }))
        setImageHasBeenEdited(true)
      } else {
        _setImg(newImg)
      }
    },
    [dispatch, user]
  )

  const canEdit = React.useMemo(() => {
    if (user?.deleted_at) {
      return false
    }

    if (hasPermission(PERMS.ContactsFull)) {
      return true
    }

    if (hasPermission(PERMS.ContactsOwn)) {
      return user?.owner_id === currentUser?.id
    }

    return false
  }, [hasPermission, user, currentUser])

  const canDelete = React.useMemo(() => {
    if (user?.deleted_at) {
      return false
    }

    if (hasPermission(PERMS.ContactsAdmin)) {
      return true
    }

    if (hasPermission(PERMS.ContactsOwn)) {
      return user?.owner_id === currentUser?.id
    }

    return false
  }, [hasPermission, user, currentUser])

  const classes = useStyles({ smallMedia: down770 })

  React.useEffect(() => {
    if (!userTypes.loaded && !userTypes.loading) {
      dispatch(fetchTypes())
    }
  }, [userTypes, dispatch])

  React.useEffect(() => {
    if (userNotFound) {
      setUserNotFound(null)
    }
    //eslint-disable-next-line
  }, [id])

  React.useEffect(() => {
    const pushLastViewed = async () => {
      await saveContactActivity(currentUser.id, env.name, id)
    }
    if (envLoaded) pushLastViewed()
  }, [env, id, currentUser, envLoaded])

  React.useEffect(() => {
    if (usersLoaded) {
      const foundUser = allUsers[id]

      if (foundUser) {
        setUser(foundUser)
        setLoading(false)
      } else {
        loadUser(id)
      }
    }
  }, [usersLoaded, allUsers, id, loadUser, loading])

  React.useEffect(() => {
    if (img) {
      setUser(vals => ({ ...vals, avatar_id: img }))
      setImageHasBeenEdited(true)
    }
  }, [setUser, img])

  const redirectToUsers = React.useCallback(
    id => {
      history.push(`${Routes.USERS}/${id}`)
    },
    [history]
  )

  const updateUser = React.useCallback(
    async (user, value, key) => {
      if (!user || !value || !key || !userTypes.types) {
        return
      }

      const copy = {
        ...user,
        [key]: value !== 'null' ? value : null
      }

      const typeObj = userTypes.types.find(type => {
        if (type.id === value) {
          return type
        }
        return null
      })

      await uploadUser(
        copy,
        false,
        () => {
          // if type was changed, change url
          if (key === 'type_id' && !typeObj.external) {
            redirectToUsers(copy.id)
          }
        },
        typeObj
      )
    },
    [uploadUser, userTypes.types, redirectToUsers]
  )

  const handleStatusChange = React.useCallback(
    newStatus => {
      setUser(old => ({
        ...old,
        status_id: newStatus,
        online_status: undefined
      }))

      updateUser(user, newStatus, 'status_id')
      dispatch(AddSuccessAlert('Changes saved'))
    },
    [user, updateUser, dispatch]
  )

  const handleTypeChange = React.useCallback(
    newType => {
      const typeName = userTypes.types.find(type => type.id === newType)

      if (typeName.name === 'User') {
        setUser(old => ({ ...old, type_id: newType }))

        // ROLE_ID IS HARDCODED FOR NOW, WILL NEED TO USE ACTUAL ROLE WHEN SYSTEM SUPPORTS**
        const addingRole = {
          ...user,
          role_id: adminRole?.id ?? null
        }

        delete addingRole.online_status

        updateUser(addingRole, newType, 'type_id')
      } else {
        setUser(old => ({ ...old, type_id: newType }))

        const copy = { ...user }
        delete copy.online_status

        updateUser(copy, newType, 'type_id')
        dispatch(AddSuccessAlert('Changes saved'))
      }
    },
    [user, updateUser, userTypes.types, adminRole, dispatch]
  )

  const handleTimezoneChange = React.useCallback(
    newTimezone => {
      setUser(old => ({
        ...old,
        timezone: newTimezone,
        online_status: undefined
      }))

      updateUser(user, newTimezone, 'timezone')
      dispatch(AddSuccessAlert('Changes saved'))
    },
    [user, updateUser, dispatch]
  )

  return (
    <AgentPage scroll centerContent>
      <div className={classes.root}>
        {userNotFound === true ? (
          <PermissionErrorPage skipAgentPage />
        ) : loading ? (
          <LoadingSpinner />
        ) : (
          <MetaProvider user={user}>
            <EditProvider>
              <QueueStateProvider>
                <ChatEditStateProvider>
                  <CustomerProvider user={user}>
                    <DetailsTopBar
                      user={user}
                      img={img}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      setImg={setImg}
                      customerVersion={true}
                      imageHasBeenEdited={imageHasBeenEdited}
                      setImageHasBeenEdited={setImageHasBeenEdited}
                    />

                    <div className={classes.container}>
                      {down770 && (
                        <UserSummary
                          user={user}
                          onStatusChange={handleStatusChange}
                          onTypeChange={handleTypeChange}
                          canEdit={canEdit}
                        />
                      )}

                      {up770 && (
                        <DetailsPane
                          user={user}
                          setImg={setImg}
                          onStatusChange={handleStatusChange}
                          onTypeChange={handleTypeChange}
                          onTimezoneChange={handleTimezoneChange}
                          canEdit={canEdit}
                        />
                      )}

                      <DetailsTable
                        user={user}
                        readOnly={false}
                        canEdit={canEdit}
                      />
                    </div>
                  </CustomerProvider>
                </ChatEditStateProvider>
              </QueueStateProvider>
            </EditProvider>
          </MetaProvider>
        )}
      </div>
    </AgentPage>
  )
}

export default ContactDetails
